import { createDefaultManifest } from '@/features/project/projectFile';
import type { ProjectManifest } from '@skyrover/shared/types';
import { create } from 'zustand';

export type ThemePreference = 'light' | 'dark' | 'system';
export type BlockOperationMode = 'beginner' | 'advanced';

interface SettingsState {
  themePreference: ThemePreference;
  blockOperationMode: BlockOperationMode;
  showCodePanel: boolean;
  bottomPanelHeight: number;
  projectName: string;
  projectManifest: ProjectManifest;
  setThemePreference: (pref: ThemePreference) => void;
  setBlockOperationMode: (mode: BlockOperationMode) => void;
  toggleCodePanel: () => void;
  setBottomPanelHeight: (height: number) => void;
  setProjectName: (name: string) => void;
  setProjectManifest: (manifest: ProjectManifest) => void;
  resetProject: (boardId: string) => void;
}

function loadThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem('skyrover-theme');
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch {
    // Private mode or blocked storage — use defaults.
  }
  return 'system';
}

function loadBlockOperationMode(): BlockOperationMode {
  try {
    const stored = localStorage.getItem('skyrover-block-mode');
    if (stored === 'beginner' || stored === 'advanced') {
      return stored;
    }
  } catch {
    // Private mode or blocked storage — use defaults.
  }
  return 'beginner';
}

const defaultBoardId = 'arduino-uno';

export const useSettingsStore = create<SettingsState>((set) => ({
  themePreference: loadThemePreference(),
  blockOperationMode: loadBlockOperationMode(),
  showCodePanel: true,
  bottomPanelHeight: 220,
  projectName: 'Untitled Project',
  projectManifest: createDefaultManifest(defaultBoardId),
  setThemePreference: (themePreference) => {
    localStorage.setItem('skyrover-theme', themePreference);
    set({ themePreference });
  },
  setBlockOperationMode: (blockOperationMode) => {
    localStorage.setItem('skyrover-block-mode', blockOperationMode);
    set({ blockOperationMode });
  },
  toggleCodePanel: () => set((s) => ({ showCodePanel: !s.showCodePanel })),
  setBottomPanelHeight: (bottomPanelHeight) => set({ bottomPanelHeight }),
  setProjectName: (projectName) => set({ projectName }),
  setProjectManifest: (projectManifest) =>
    set({ projectManifest, projectName: projectManifest.name }),
  resetProject: (boardId) => {
    const manifest = createDefaultManifest(boardId);
    set({ projectName: manifest.name, projectManifest: manifest });
  },
}));

export function resolveThemeMode(preference: ThemePreference): 'light' | 'dark' {
  if (preference === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return preference;
}
